import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'hooks';
import { useTableSelector } from 'hooks/useTableSelector';

import { useSitGoResultEntry } from 'components/SitGoResult/useSitGoResult';

import {
  computeStartsInSeconds,
  resolveStartsInDisplay
} from '../UserActions/TournamentScreenModal';

import { FELT_LINE_GAP, feltMetaText, feltPrimaryText, feltSecondaryText } from './feltTypography';
import { compactChips, formatCountdown, toChips } from './TournamentTableDetails';

/**
 * "sb/bb (ante)" with each value abbreviated (100000 → "100K"). The ante is only
 * appended when non-zero — matching legacy parity, where a zero-ante level reads
 * "2/4" rather than "2/4 (0)". Values may arrive as comma-grouped strings, so the
 * ante-presence test goes through toChips rather than a raw Number() coercion.
 */
const formatBlindLevel = (sb: any, bb: any, ante: any): string =>
  `${compactChips(sb)}/${compactChips(bb)}${toChips(ante) ? ` (${compactChips(ante)})` : ''}`;

const TableDetails = () => {
  const blindLevels = useTableSelector((table) => table.game.blindLevels);
  const tableType = useTableSelector((table) => table.game.tabletype);
  const game = useTableSelector((table) => table.game);
  const tournamentStatus = useTableSelector((table) => table.tournamentStatus);

  // Not loaded yet: tabletype only becomes 's' (cash) or 'm'/'t' (tournament)
  // once the first table state (gotTexas/gotOmaha) lands. Render NOTHING here —
  // the tournament view would otherwise show its zero-value placeholders
  // ("Break · 00:00:00 / 0/0 / Next: 0/0") on a cash table while loading. The
  // HJ chip load indicator is rendered by Table (outside the felt's 3D tilt) so
  // it stays flat/round like the Tournament Final Balancing spinner.
  if (!tableType) {
    return null;
  }

  // For cash games, just show blind levels
  if (tableType === 's') {
    return <div style={containerStyle}>{blindLevels}</div>;
  }

  // Tournament — consolidated view
  return <TournamentInfo game={game} tournamentStatus={tournamentStatus} />;
};

const TournamentInfo = ({ game, tournamentStatus }: { game: any; tournamentStatus: any }) => {
  const [localCountdown, setLocalCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // EN-11313 (release.sit-go-result-modal): a finished Sit & Go has no level
  // left to count down, so its clock stops where it was, under the felt's Go
  // to lobby chip. Always null with the flag off, and a busted player at a
  // table still in play has an entry that is not finished: both keep ticking.
  const sitGoResult = useSitGoResultEntry();
  const clockStopped = !!sitGoResult && (sitGoResult.tableFinished || game?.status === 'F');

  // Pre-start ("Tournament starts in …") countdown, shown in place of the
  // level line before the first hand. Without this the felt reads
  // "LVL 1 · 00:00:00" and looks frozen — the engine hasn't started ticking
  // the level countdown yet because the tournament hasn't begun. Mirrors the
  // seed-once-and-tick pattern from TournamentScreenModal so the display is
  // immune to the ~13s pre-start status churn and heartbeat glitches.
  const startAtTime = Number(game?.startAtTime) || 0;
  const serverTime = Number(useAppSelector((state: any) => state.serverDetails?.serverTime)) || 0;
  const startsInFromServer = computeStartsInSeconds({
    schedule: game?.schedule,
    startAtTime,
    serverTime
  });
  const startsInCountdownOver =
    game?.schedule !== 'Rolling' &&
    startAtTime > 0 &&
    serverTime > 0 &&
    startAtTime - serverTime <= 0;
  const [startsInDisplay, setStartsInDisplay] = useState(0);
  const startsInOverRef = useRef(false);
  // Reset the pre-start latch on tournament identity change so a freshly
  // opened table can seed its own countdown.
  useEffect(() => {
    startsInOverRef.current = false;
    setStartsInDisplay(0);
  }, [game?.tournamentId, game?.gameID]);
  useEffect(() => {
    if (startsInCountdownOver) startsInOverRef.current = true;
    setStartsInDisplay((local) =>
      resolveStartsInDisplay(
        local,
        startsInFromServer,
        startsInCountdownOver,
        startsInOverRef.current
      )
    );
  }, [startsInFromServer, startsInCountdownOver]);
  const isStartsInTicking = startsInDisplay > 0;
  useEffect(() => {
    if (!isStartsInTicking) return;
    const id = setInterval(() => setStartsInDisplay((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(id);
  }, [isStartsInTicking]);

  const parseCountdown = useCallback((val: any): number => {
    if (typeof val === 'number') return val;
    if (typeof val !== 'string' || val === '-') return 0;
    const parts = val.split(':').map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return 0;
  }, []);

  // Sync from tournament status
  useEffect(() => {
    const data = Array.isArray(tournamentStatus) ? tournamentStatus : [];
    const match = data.find((d: any) => d.tournamentId === game?.tournamentId);
    if (match?.levelCountDown) {
      const serverVal = parseCountdown(match.levelCountDown);
      setLocalCountdown((local) => {
        if (local === 0) return serverVal;
        // EN-11313: stopped, the clock keeps its value. Only a panel that
        // mounts on an already finished table takes its first reading above.
        if (clockStopped) return local;
        if (serverVal >= local) return serverVal;
        if (local - serverVal > 2) return serverVal;
        return local;
      });
    }
  }, [tournamentStatus, game?.tournamentId, parseCountdown, clockStopped]);

  // Tick locally
  useEffect(() => {
    if (clockStopped) return undefined;
    timerRef.current = setInterval(() => {
      setLocalCountdown((v) => Math.max(0, v - 1));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [clockStopped]);

  const isBreak = game.lvlDisplay === 0;
  const blindsText = game.isMultiFlightBreak
    ? '-'
    : formatBlindLevel(game.currentSB, game.currentBB, game.currentAnte);

  const nextBlindsText = game.isMultiFlightBreak
    ? '-'
    : game.isOnBreak && game.isMultiFlight === 1 && game.isFinalMultiFlight === 1
    ? formatBlindLevel(game.sb, game.bb, game.ante)
    : game.nextLevelIsBreak === 1
    ? 'Break'
    : Number(game?.nextLevel) !== -1
    ? formatBlindLevel(game.nextSB, game.nextBB, game.nextAnte)
    : '';

  return (
    <div style={containerStyle}>
      {game.title ? <span style={nameStyle}>{game.title}</span> : null}
      <span style={levelText}>
        {isStartsInTicking ? (
          <>Starts in {formatCountdown(startsInDisplay)}</>
        ) : (
          <>
            {isBreak ? 'Break' : `Lvl ${game.lvlDisplay}`}
            {' · '}
            {Number(game?.nextLevel) === -1 ? 'Max' : formatCountdown(localCountdown)}
          </>
        )}
      </span>
      <span style={blindsStyle}>{blindsText}</span>
      {nextBlindsText && <span style={nextStyle}>Next: {nextBlindsText}</span>}
    </div>
  );
};

export default TableDetails;

// EN-10520 — the blinds / game-type line is one row of the shared felt type
// system (feltTypography). It no longer carries its own family, colour or
// `transform: scale(...)`: <FeltMeta /> owns the scale and the line rhythm for
// the whole under-board group, so this and the table/hand number can't drift
// apart or double-scale.
const containerStyle: CSSProperties = {
  ...feltPrimaryText,
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: FELT_LINE_GAP
};

// The tournament rows below are now nothing but the shared tokens. They used to
// carry their own sizes (0.95 / 0.78 / 1 / 0.72rem), their own weights, their
// own tracking and their own case — which is why a tournament felt looked like a
// different design from a cash one sitting next to it in the grid. Size and
// weight are the only things that separate these rows now; colour, family,
// tracking and case all come from feltTextBase.

/** Tournament name — same rank as the blinds line it heads. */
const nameStyle: CSSProperties = feltPrimaryText;

/** Level + countdown. */
const levelText: CSSProperties = feltSecondaryText;

/** Blinds — the same treatment as a cash table's blinds/game-type line. */
const blindsStyle: CSSProperties = feltPrimaryText;

/** Upcoming level — the quietest row, like the table/hand number. */
const nextStyle: CSSProperties = feltMetaText;
